import React, { useState, useCallback, useEffect, useRef } from 'react';
import Card from './Card';
import { sortByColor, sortByNumber } from '../utils/cardHelpers';

/*
  DEAL-IN ANIMATION LOGIC
  ─────────────────────────────────────────────────────────────────────────────
  When `dealAnimation` prop is true (set by parent on game start / new round),
  each card gets the `.card-deal-in` class with staggered --deal-delay CSS vars.
  The CSS keyframe flies cards from top-left (approx draw pile region) to the
  hand slots, with a slight overshoot bounce. After the last card lands, the
  dealing state clears automatically.
*/
const DEAL_STAGGER_MS = 75;   // ms between each card launch
const DEAL_DUR_MS     = 520;  // single card flight duration

function PlayerHand({
  cards,
  selectedCards = [],
  onCardSelect,
  onCardClick,
  disabled = false,
  canSelect = true,
  maxSelect = Infinity,
  showSort = true,
  highlightCardId = null,
  hittableCardIds = new Set(),
  onSortModeChange = null,
  dealAnimation = false,     // ← NEW: triggers deal-in effect
}) {
  const [sortMode, setSortMode] = useState('color');
  const [isDealing, setIsDealing] = useState(false);

  // Track previous dealAnimation flag to detect rising edge
  const prevDealRef = useRef(false);

  useEffect(() => {
    // Trigger only on the rising edge (false → true)
    if (dealAnimation && !prevDealRef.current && cards.length > 0) {
      setIsDealing(true);
      // Clear dealing state after all cards have finished animating
      const totalMs = DEAL_STAGGER_MS * (cards.length - 1) + DEAL_DUR_MS + 50;
      const t = setTimeout(() => setIsDealing(false), totalMs);
      prevDealRef.current = true;
      return () => clearTimeout(t);
    }
    if (!dealAnimation) {
      prevDealRef.current = false;
    }
  }, [dealAnimation, cards.length]);

  const getDisplayCards = () => {
    switch (sortMode) {
      case 'color':  return sortByColor(cards);
      case 'number': return sortByNumber(cards);
      default:       return sortByColor(cards);
    }
  };

  const displayCards = getDisplayCards();

  const cycleSortMode = () => {
    const newMode = sortMode === 'color' ? 'number' : 'color';
    setSortMode(newMode);
    if (onSortModeChange) onSortModeChange(newMode);
  };


  const handleCardClick = useCallback((card) => {
    if (disabled) return;
    if (onCardClick) { onCardClick(card); return; }
    if (canSelect && onCardSelect) {
      const isSelected = selectedCards.some(c => c.id === card.id);
      if (isSelected) {
        onCardSelect(selectedCards.filter(c => c.id !== card.id));
      } else if (maxSelect === 1) {
        onCardSelect([card]);
      } else if (selectedCards.length < maxSelect) {
        onCardSelect([...selectedCards, card]);
      }
    }
  }, [disabled, onCardClick, canSelect, onCardSelect, selectedCards, maxSelect]);

  const isSelected = (card) => selectedCards.some(c => c.id === card.id);

  return (
    <div className="relative flex flex-col items-center w-full">
      {/* Sort toggle - Top Left Corner icon (Moved up to prevent overlap) */}
      {showSort && cards.length > 0 && (
        <button
          onClick={cycleSortMode}
          title={`Currently sorted by ${sortMode}. Click to switch sort mode.`}
          className="absolute left-1 -top-10 sm:left-2 sm:-top-12 z-[50] flex items-center justify-center text-[1.4rem] sm:text-[1.8rem] text-[rgba(212,168,67,0.65)] hover:text-[#d4a843] transition-colors active:scale-90 cursor-pointer"
          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
        >
          🔄
        </button>
      )}

      {/* Cards grid */}
      <div
        className="flex flex-wrap justify-center p-1 sm:p-2 pt-3"
        style={{ maxWidth: '100%', gap: '0px' }}
      >
        {displayCards.map((card, index) => {
          const isHighlighted = card.id === highlightCardId;
          const isHittable    = hittableCardIds.has(card.id);
          const selected      = isSelected(card);

          // Slight angle variation per card for organic feel
          const dealRot = -30 + (index % 3) * 8;

          // Deal-in CSS custom properties
          const dealStyle = isDealing ? {
            '--deal-delay': `${index * DEAL_STAGGER_MS}ms`,
            '--deal-dur':   `${DEAL_DUR_MS}ms`,
            '--deal-rot':   `${dealRot}deg`,
            // Deck is in the upper-area center of the game; hand is at bottom.
            // dx stays near 0 (horizontal), dy is large negative (from top).
            '--deal-dx':    `${-2 + (index % 5 - 2) * 3}vw`,
            '--deal-dy':    '-52vh',
          } : {};

          return (
            <div
              key={card.id}
              className={[
                'relative transition-transform duration-150',
                isHighlighted ? 'animate-pulse-glow' : '',
                isHittable
                  ? 'ring-2 ring-cyan-400 ring-offset-1 ring-offset-transparent rounded-lg shadow-[0_0_10px_rgba(34,211,238,0.5)]'
                  : '',
                // Apply deal-in class only while dealing
                isDealing ? 'card-deal-in' : '',
              ].filter(Boolean).join(' ')}
              style={{
                ...dealStyle,
                transform: !isDealing && selected ? 'translateY(-10px)' : undefined,
              }}
            >
              {isHighlighted && (
                <div className="absolute -top-4 sm:-top-5 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs bg-green-500 text-white px-1.5 sm:px-2 py-0.5 rounded font-bold whitespace-nowrap z-20 shadow-lg">
                  NEW
                </div>
              )}
              {isHittable && !isHighlighted && (
                <div className="absolute -top-4 sm:-top-5 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs bg-cyan-500 text-white px-1.5 sm:px-2 py-0.5 rounded font-bold whitespace-nowrap z-20 shadow-lg">
                  HIT
                </div>
              )}
              <Card
                card={card}
                index={index}
                selected={selected}
                highlighted={isHighlighted}
                onClick={() => handleCardClick(card)}
                disabled={disabled}
                draggable={!disabled && canSelect}
                noShadow={true}
                dragPayload={selected && selectedCards.length > 1 ? { card, cards: selectedCards, source: 'hand' } : { card, index, source: 'hand' }}
              />
            </div>
          );
        })}
      </div>

      {/* Selection info */}
      {canSelect && selectedCards.length > 0 && (
        <div className="text-xs sm:text-sm text-accent-gold">
          {selectedCards.length} selected
        </div>
      )}

      {/* Empty hand */}
      {cards.length === 0 && (
        <div className="text-gray-400 text-center py-4 sm:py-8 text-sm">
          No cards in hand
        </div>
      )}
    </div>
  );
}

// Compact hand display for other players
export function CompactHand({ cardCount, isCurrentTurn = false }) {
  return (
    <div className={`flex items-center gap-1 ${isCurrentTurn ? 'current-turn rounded-lg p-2' : ''}`}>
      {Array.from({ length: Math.min(cardCount, 10) }).map((_, i) => (
        <div
          key={i}
          className="w-4 h-6 bg-gradient-to-br from-indigo-800 to-purple-900 rounded border border-indigo-400"
          style={{ marginLeft: i > 0 ? '-8px' : '0', zIndex: i }}
        />
      ))}
      {cardCount > 10 && (
        <span className="text-xs text-gray-400 ml-1">+{cardCount - 10}</span>
      )}
      <span className="ml-2 text-sm text-gray-300">{cardCount} cards</span>
    </div>
  );
}

// Fan-style hand for more visual appeal
export function FanHand({
  cards,
  selectedCards = [],
  onCardSelect,
  disabled = false,
  maxWidth = 600
}) {
  const cardCount   = cards.length;
  const totalSpread = Math.min(cardCount * 8, 120);

  return (
    <div
      className="relative flex justify-center items-end"
      style={{ height: '150px', width: maxWidth }}
    >
      {cards.map((card, index) => {
        const centerOffset = index - (cardCount - 1) / 2;
        const rotation     = centerOffset * (totalSpread / cardCount);
        const translateY   = Math.abs(centerOffset) * 2;
        const isSelected   = selectedCards.some(c => c.id === card.id);

        return (
          <div
            key={card.id}
            className="absolute transition-all duration-200 hover:z-50"
            style={{
              transform: `rotate(${rotation}deg) translateY(${isSelected ? translateY - 20 : translateY}px)`,
              zIndex: isSelected ? 100 : index,
              left: `${50 + centerOffset * 25}%`,
              transformOrigin: 'bottom center',
            }}
          >
            <Card
              card={card}
              index={index}
              selected={isSelected}
              onClick={() => {
                if (!disabled && onCardSelect) {
                  const newSelected = isSelected
                    ? selectedCards.filter(c => c.id !== card.id)
                    : [...selectedCards, card];
                  onCardSelect(newSelected);
                }
              }}
              disabled={disabled}
              draggable={!disabled}
            />
          </div>
        );
      })}
    </div>
  );
}

export default PlayerHand;
